import { useEffect } from 'react';

import { useMarketQuery } from '#/api/queries/marketsQueries';
import {
  useActiveWindowInterval,
  useWebsocketEventListener,
} from '#/components/hooks';
import {
  getMarketsPollDelayMs,
  MARKETS_POLL_FAST_DELAY_MS,
} from '#/components/markets/intermittent/intermittentMarkets';
import { RELOAD_MARKETS_DELAY_MS } from '#/config/constants';
import { loadHiddenMarket } from '#/reduxState/markets/listed/listedMarketsActions';
import { fetchAndDispatchMarkets } from '#/reduxState/markets/listed/listedMarketsUtils';
import {
  getStateOutsideOfComponent,
  staticDispatch,
  useSelector,
} from '#/reduxState/store';

/**
 * Special case when we did not reveal market yet but want to see it on Dashboard.
 * It's only accessible by directly quering the API by market param.
 */
function useUnlistedMarketFetcher() {
  const currentMarketPair = useSelector(
    state => state.markets.currentMarket.pair
  );
  const loadedForLoggedIn = useSelector(
    state => state.markets.listedMarkets.loadedForLoggedIn
  );
  const listedMarketsList = useSelector(
    state => state.markets.listedMarkets.listedMarketsList
  );
  /**
   * Special case when we did not reveal market yet but want to see it on Dashboard.
   * It's only accessible by directly quering the API by market param.
   */
  const needToFetchHiddenMarket =
    loadedForLoggedIn !== null &&
    currentMarketPair &&
    !listedMarketsList.includes(currentMarketPair);

  const { data: hiddenMarketData } = useMarketQuery(
    needToFetchHiddenMarket ? currentMarketPair : undefined
  );
  useEffect(() => {
    if (!hiddenMarketData) {
      return;
    }
    staticDispatch(loadHiddenMarket(hiddenMarketData));
  }, [hiddenMarketData]);
}

/**
 * Polls the markets API as a fallback for missed websocket
 * `exchange_status_updated` events (flaky connections): every 30s, tightened
 * to 10s around intermittent market session transitions. Runs only while the
 * tab is visible; returning to a stale tab catches up right away
 * (`useActiveWindowInterval`). Any other reload (websocket event, GEO
 * refetch) updates `loadedAtMs`, so the poll skips instead of stacking an
 * extra fetch on top of it.
 */
function useMarketsPolling() {
  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const loadedAtMs = useSelector(
    state => state.markets.listedMarkets.loadedAtMs
  );
  const marketsTradingInfo = useSelector(
    state => state.markets.listedMarkets.marketsTradingInfo
  );

  // Ticks at the fast cadence; each tick decides whether the data age
  // already calls for a refetch
  useActiveWindowInterval(() => {
    // Initial load is handled by useMarketsAsyncFetcher
    if (loadedAtMs === null) {
      return;
    }
    const nowMs = Date.now();
    const delayMs = getMarketsPollDelayMs(marketsTradingInfo, nowMs);
    // Data fresh enough — eg. just reloaded by a websocket event
    if (nowMs - loadedAtMs < delayMs) {
      return;
    }
    fetchAndDispatchMarkets(staticDispatch, loggedIn);
  }, MARKETS_POLL_FAST_DELAY_MS);
}

/**
 * Fetch markets list when it's not yet loaded (persists until refresh)
 */
export function useMarketsAsyncFetcher() {
  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const userInitialized = useSelector(state => state.currentUser.initialized);
  const loadedForLoggedIn = useSelector(
    state => state.markets.listedMarkets.loadedForLoggedIn
  );

  useEffect(() => {
    if (!userInitialized) {
      return;
    }
    // Not loaded at all
    if (loadedForLoggedIn === null) {
      fetchAndDispatchMarkets(staticDispatch, loggedIn);
      return;
    }

    // Loaded, but user was changed (which can lead to different market list because of GEO restrictions)
    if (loadedForLoggedIn !== loggedIn) {
      fetchAndDispatchMarkets(staticDispatch, loggedIn);
    }
  }, [loadedForLoggedIn, loggedIn, userInitialized]);

  // Also fetch unlisted markets when directly visited by entering dashboard URL
  useUnlistedMarketFetcher();

  // Keep session states fresh even when websocket events are missed
  useMarketsPolling();
}

/**
 * This updated needs to be always runnning to not miss any reaload events
 *
 * Matches any market related exchange status action — normal market status
 * changes as well as `action=controls_market`, which the engine emits on
 * intermittent market session transitions (open/close).
 */
export function useListenOnWebsocketReloadMarkets() {
  const loggedIn = useSelector(state => state.currentUser.loggedIn);

  useWebsocketEventListener('webclient', message => {
    if (
      !(
        message.data.event === 'exchange_status_updated' &&
        message.data.action?.includes('market')
      )
    ) {
      return;
    }
    console.log('Exchange status updated - reloading markets');
    setTimeout(() => {
      getStateOutsideOfComponent().then(state => {
        if (state.currentUser.loggedIn !== loggedIn) {
          console.warn('User state changed - ignoring "reload_markets" event');
          return;
        }
        fetchAndDispatchMarkets(staticDispatch, loggedIn);
      });
    }, RELOAD_MARKETS_DELAY_MS);
  });
}
